import React from 'react';
import Button from '@amzn/awsui-components-console/button';
import Popover from '@amzn/awsui-components-console/popover';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import {
  EDIT_GROUP_PENDING_CHANGES_LOCK_REASON_KEY,
  EDIT_GROUP_NOT_SUBMITTED_LOCK_REASON_KEY,
  EDIT_GROUP_IN_PROGRESS_LOCK_REASON_KEY,
  SUBMISSION_PENDING_LOCK_REASON_KEY,
} from '../constants';
import styles from '../StringGroupDetail.module.scss';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface PageHeaderProps {
  title: string;
  onVersionHistoryClick: () => void;
  onEditGroupClick: () => void;
  /**
   * When false, editing controls are disabled (grayed out). This happens for
   * string groups owned by clients other than LangHub.
   */
  canEdit?: boolean;
  /**
   * Whether the group is owned by LangHub. When false (a group ingested from a
   * non-LangHub client such as TranscomFileUI), the Version History and Edit
   * Group controls are not rendered at all — the group is view-only in LangHub,
   * so those actions have no meaning and are hidden rather than shown disabled.
   */
  isEditableClient?: boolean;
  /**
   * Whether the permission answer is still in flight. Gated controls show a skeleton rather
   * than a disabled state, so they never claim a missing permission before it is known.
   */
  permissionsLoading?: boolean;
  /**
   * When true, a submission is awaiting TRANSCOM confirmation, so Edit Group is
   * locked with a visible reason on hover — group-level edits are paused until
   * the in-flight submission is confirmed.
   */
  submissionPending?: boolean;
  /**
   * When true, the draft already holds string-level changes (added/edited/
   * deleted strings). Editing the group chains into the submit dialog whose
   * cancel discards the entire draft, so Edit Group is locked with a visible
   * reason until those pending changes are submitted or discarded.
   */
  editGroupLockedByPendingChanges?: boolean;
  /**
   * When true, the group has never been submitted for translation (M1). Editing
   * chains into the submit dialog, so it is disabled until the first submission
   * exists — see EDIT_GROUP_NOT_SUBMITTED_LOCK_REASON_KEY.
   */
  groupNeverSubmitted?: boolean;
  /**
   * When true, a translation job is currently in progress. Editing chains into a
   * new submission that would start a second job on top of the in-flight one, so
   * Edit Group is locked until the current job is cancelled or completes — see
   * EDIT_GROUP_IN_PROGRESS_LOCK_REASON_KEY.
   */
  translationInProgress?: boolean;
  /** Optional status indicator (e.g. "Unsaved changes") shown inline in the title row. */
  statusBadge?: React.ReactNode;
  /**
   * When true, the group name may still be corrected in place (a not-yet-
   * submitted group whose name is only a local identifier). Surfaces an inline
   * edit control beside the title; ignored unless {@link onRenameClick} is set.
   */
  canRename?: boolean;
  /** Opens the rename flow. Rendered only when {@link canRename} is true. */
  onRenameClick?: () => void;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  onEditGroupClick,
  onVersionHistoryClick,
  canEdit = true,
  isEditableClient = true,
  permissionsLoading = false,
  submissionPending = false,
  editGroupLockedByPendingChanges = false,
  groupNeverSubmitted = false,
  translationInProgress = false,
  statusBadge,
  canRename = false,
  onRenameClick,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>
          {title}
          <span
            className={styles.infoIcon}
            title={t('sgd-header-help')}
            aria-label={t('sgd-header-help')}
            role="img"
          >
            ⓘ
          </span>
          {canRename && onRenameClick && (
            <Button
              variant="inline-icon"
              iconName="edit"
              ariaLabel={t('sgd-rename-title')}
              onClick={onRenameClick}
            />
          )}
        </h1>
        {/*
        Version History and Edit Group are only meaningful for LangHub-owned
        groups. A group ingested from another client (e.g. TranscomFileUI) is
        view-only in LangHub, so these controls are hidden entirely rather than
        rendered disabled — the read-only info Alert on the page explains why.
      */}
        {isEditableClient && (
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onVersionHistoryClick}>
              {t('string-version-history')}
            </Button>
            {permissionsLoading ? (
              <>
                <Skeleton display="inline-block" height="32px" width="105px" />
              </>
            ) : submissionPending && canEdit ? (
              // Locked by an in-flight submission (not a permission/ownership gate),
              // so surface the reason on hover the same way the action bar does.
              <Popover
                content={t(SUBMISSION_PENDING_LOCK_REASON_KEY)}
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
              >
                <Button disabled>{t('sgd-edit-group')}</Button>
              </Popover>
            ) : editGroupLockedByPendingChanges && canEdit ? (
              // Locked because the draft holds staged string changes that the group-
              // edit → submit → cancel path would discard. Surface why on hover.
              <Popover
                content={t(EDIT_GROUP_PENDING_CHANGES_LOCK_REASON_KEY)}
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
              >
                <Button disabled>{t('sgd-edit-group')}</Button>
              </Popover>
            ) : translationInProgress && canEdit ? (
              // A translation is in flight: editing would chain into a second
              // submission (V2) on top of it. Block it with the reason on hover.
              <Popover
                content={t(EDIT_GROUP_IN_PROGRESS_LOCK_REASON_KEY)}
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
              >
                <Button disabled>{t('sgd-edit-group')}</Button>
              </Popover>
            ) : groupNeverSubmitted && canEdit ? (
              // The group has never been submitted for translation (M1): editing is
              // only meaningful once a first submission exists. Explain on hover.
              <Popover
                content={t(EDIT_GROUP_NOT_SUBMITTED_LOCK_REASON_KEY)}
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
              >
                <Button disabled>{t('sgd-edit-group')}</Button>
              </Popover>
            ) : (
              <Button
                disabled={
                  !canEdit ||
                  submissionPending ||
                  editGroupLockedByPendingChanges ||
                  translationInProgress ||
                  groupNeverSubmitted
                }
                onClick={onEditGroupClick}
              >
                {t('sgd-edit-group')}
              </Button>
            )}
          </SpaceBetween>
        )}
        {statusBadge}
      </div>
      <p className={styles.pageDescription}>{t('sgd-header-description')}</p>
    </div>
  );
};

export default PageHeader;
